// transport-config-helpers.js
//
// Pure helpers for the configuration step of the package transport wizard. The older ones were
// ported from the modal wizard in IntegrationContent.jsx; effektiverBasiswert and
// buildConfigsByWerk belong to the snapshot contract and have no counterpart there.
//
// No React, no hooks, no stores, no imports, so the module stays testable and tree-shakeable.
// Defensive against null/undefined input.

/**
 * Baut den Cache-Key für eine Artefakt-Konfiguration so wie der Modal-Wizard
 * (IntegrationContent.jsx Z. 1153).
 *
 * Format:
 *   - mit werkKennung: "artId__werkKennung"
 *   - ohne werkKennung, mit targetId: "artId__targetId"
 *   - sonst: "artId"
 *
 * @param {string} artId            Artefakt-ID (z.B. Flow-ID).
 * @param {string} [targetId]       Ziel-Instanz-ID, optional.
 * @param {string} [werkKennung]    Werk-Kennung (z.B. "werk__kennung"), optional.
 *                                  Hat Vorrang vor targetId.
 * @returns {string} Cache-Key oder "" falls artId fehlt.
 *
 * @example
 *   getArtifactCacheKey("Flow1", "tgt-1");           // "Flow1__tgt-1"
 *   getArtifactCacheKey("Flow1", "tgt-1", "werk_DE"); // "Flow1__werk_DE"
 *   getArtifactCacheKey("Flow1");                     // "Flow1"
 */
export function getArtifactCacheKey(artId, targetId, werkKennung) {
  if (!artId) return "";
  if (werkKennung) return `${artId}__${werkKennung}`;
  if (targetId) return `${artId}__${targetId}`;
  return String(artId);
}

/**
 * Normalisiert einen Configurations-Eintrag aus dem Backend zur Modal-Form
 * (IntegrationContent.jsx Z. 1166-1170).
 *
 * Hinzugefuegte Felder:
 *   - newValue:     targetValue || ""
 *   - fromTarget:   !!targetValue
 *   - defaultValue: ParameterValue
 *   - modified:     false (Initialzustand vor User-Edit)
 *
 * @param {object|null|undefined} raw Roh-Eintrag vom Backend.
 * @returns {object|null} Normalisierter Eintrag oder null falls raw leer.
 *
 * @example
 *   normalizeConfigEntry({ ParameterKey: "host", ParameterValue: "x", targetValue: "y" })
 *   // -> { ParameterKey:"host", ParameterValue:"x", targetValue:"y",
 *   //      newValue:"y", fromTarget:true, defaultValue:"x", modified:false }
 */
export function normalizeConfigEntry(raw) {
  if (!raw || typeof raw !== "object") return null;
  const targetValue = raw.targetValue;
  return {
    ...raw,
    newValue: targetValue || "",
    fromTarget: !!targetValue,
    defaultValue: raw.ParameterValue,
    modified: false,
  };
}

/**
 * Klassifiziert den Status einer Config-Zeile für das UI-Label.
 *
 * Reihenfolge der Regeln (frueher Match gewinnt):
 *   1. !newValue UND group in {sender,receiver} -> "required"
 *      (Pflichtfeld blockiert Transport, vgl. Z. 1212)
 *   2. !newValue                                -> "empty"
 *   3. fromTarget UND newValue===targetValue    -> "fromTarget"
 *   4. newValue !== defaultValue                -> "modified"
 *   5. sonst                                    -> "default"
 *
 * @param {object|null|undefined} c Normalisierter Config-Eintrag.
 * @returns {"required"|"fromTarget"|"modified"|"default"|"empty"}
 *
 * @example
 *   classifyConfigStatus({ group:"sender", newValue:"" });
 *   // -> "required"
 *   classifyConfigStatus({ group:"other", newValue:"x", defaultValue:"x" });
 *   // -> "default"
 */
export function classifyConfigStatus(c) {
  if (!c || typeof c !== "object") return "empty";
  const group = c.group;
  const newValue = c.newValue;
  const fromTarget = !!c.fromTarget;
  const defaultValue = c.defaultValue;
  const targetValue = c.targetValue;
  // hasTargetValue: Parameter existiert bereits im Ziel-Tenant (Backend hat
  // targetValue geliefert). Solche Parameter sind NIE Pflichtfeld - der
  // bestehende Wert am Ziel bleibt erhalten wenn der User nichts angibt.
  const hasTargetValue = targetValue !== undefined && targetValue !== null && targetValue !== "";
  if (!newValue) {
    if ((group === "sender" || group === "receiver") && !hasTargetValue && !fromTarget) return "required";
    return "empty";
  }
  if (fromTarget && newValue === targetValue) return "fromTarget";
  if (newValue !== defaultValue) return "modified";
  return "default";
}

/**
 * The value that applies to the first target stage and is inherited by all later stages.
 *
 * Sender and receiver parameters deliberately do NOT inherit the source value: an address from
 * development would simply be wrong in the target, so the field stays empty and shows up as a
 * required field.
 *
 * @param {object} c Normalized config entry.
 * @returns {string} Base value, "" if none applies.
 */
export function effektiverBasiswert(c) {
  if (!c) return "";
  if (c.newValue) return c.newValue;
  const istAdresse = c.group === "sender" || c.group === "receiver";
  return istAdresse ? "" : (c.defaultValue || "");
}

/**
 * Builds the parameter structure the cicd-snapshot endpoint expects as `configsByWerk`.
 *
 * Target shape:
 *   {
 *     "<kennung>" | "": {                       // "" = no plant split
 *       "<source-artifact-id>": [
 *         { ParameterKey, DataType, Description,
 *           values: { "__base__": "...", "<stageKey>": "..." } }
 *       ]
 *     }
 *   }
 *
 * The backend turns this into SAP's file: `__base__` becomes ParameterValues.Default, every stage
 * key becomes the GitHub environment name (qas -> QAS, preprod -> PRE-PROD).
 *
 * Two intentional differences from the earlier overlay format:
 *  - The plant level sits OUTERMOST instead of inside the file, because in Git every plant gets
 *    its own package folder with its own file.
 *  - EVERY parameter that has a value is written, not just the changed ones: SAP's read rule falls
 *    back to ParameterValues.Default, and without it the parameter stays unset in the target.
 *
 * Stage values equal to the base value are left out - they would only bloat the Git diff and SAP's
 * fallback yields the same value. A `null` value, by contrast, is the tombstone: the stage was in
 * Git and the user cleared it, so the backend deletes it. A MISSING key still means "not reported,
 * keep it"; without that distinction a removed deviation would stay forever.
 *
 * @param {object} state    TransportV2 reducer state (state.cfg and state.gitCfg).
 * @param {Array}  [stages] Forward stages from vorwaertsStufen(state). The first one is the base.
 * @param {object} [gitCfg] Preloaded Git state; without it state.gitCfg is used.
 * @returns {object} Parameter map per plant (empty when nothing is configured).
 */
export function buildConfigsByWerk(state, stages, gitCfg) {
  const out = {};
  const cfg = state && state.cfg;
  if (!cfg || typeof cfg !== "object") return out;
  const stufen = Array.isArray(stages) ? stages : [];
  // First forward stage = base. All later ones are possible deviations.
  const overlayStufen = stufen.slice(1);
  const git = gitCfg || (state && state.gitCfg) || {};
  for (const [werkKey, artefactMap] of Object.entries(cfg)) {
    // "std" means no plant split. In the backend that is the empty kennung.
    const kennung = werkKey === "std" ? "" : werkKey;
    for (const [artId, item] of Object.entries(artefactMap || {})) {
      const entries = (item && item.entries) || [];
      const params = [];
      for (const c of entries) {
        if (!c || !c.ParameterKey) continue;
        const values = {};
        const basis = effektiverBasiswert(c);
        if (basis !== "") values.__base__ = basis;
        // What is already in Git for this parameter? Only that tells "deleted" apart from
        // "never maintained".
        const inGit = git?.[werkKey]?.[artId]?.[c.ParameterKey]?.values || null;
        for (const st of overlayStufen) {
          const wert = c.byStage && c.byStage[st.key] ? c.byStage[st.key].newValue : "";
          const leer = wert == null || wert === "" || wert === basis;
          if (leer) {
            // Tombstone: the stage was in Git and is now empty, so the user removed the
            // deviation on purpose. Without this null the old value would stay and the stage
            // would silently keep running a value nobody wants any more.
            if (inGit && inGit[st.key] != null) values[st.key] = null;
            continue;
          }
          values[st.key] = wert;
        }
        if (!Object.keys(values).length) continue;
        params.push({
          ParameterKey: c.ParameterKey,
          DataType: c.DataType || "xsd:string",
          Description: c.Description || "",
          values,
        });
      }
      if (!params.length) continue;
      if (!out[kennung]) out[kennung] = {};
      out[kennung][artId] = params;
    }
  }
  return out;
}

/**
 * Pflichtfeld-Validierung analog zur Pipeline-Prüfung im Modal
 * (IntegrationContent.jsx Z. 1207-1217).
 *
 * Pflichtfeld = Eintrag mit group in {sender,receiver} und leerem newValue.
 *
 * @param {Array|null|undefined} cfgs Liste normalisierter Configs.
 * @returns {{ok:boolean, missing:string[]}} ok=true wenn alles ausgefüllt,
 *          missing enthält die ParameterKeys der leeren Pflichtfelder.
 *
 * @example
 *   validateRequiredFields([
 *     { ParameterKey:"host", group:"sender",   newValue:"" },
 *     { ParameterKey:"k",    group:"receiver", newValue:"x" }
 *   ]);
 *   // -> { ok:false, missing:["host"] }
 */
export function validateRequiredFields(cfgs) {
  // Keine clientseitige Pflichtfeld-Validierung mehr - die "Sender/Receiver ohne
  // Wert = Pflicht"-Heuristik war zu strikt. SAP CPI erlaubt viele optionale
  // Sender/Receiver-Parameter (z.B. Location ID, optionale Headers). Echte
  // Pflichtfelder werden vom Ziel-Tenant beim Transport-Call validiert und
  // fuehren dort zu einem klaren Fehler. UI-Indikator "required" bleibt als
  // Empfehlung erhalten (in classifyConfigStatus), blockt aber nicht mehr.
  return { ok: true, missing: [] };
}

/**
 * Buckets Configs nach group in genau drei Sammeltoepfe: sender, receiver, other.
 * Bewusst KEIN processing- und KEIN credentials-Bucket (UI-Entscheidung im Modal-
 * Wizard - diese Gruppen sollen nicht eigenstaendig sichtbar sein).
 *
 * @param {Array|null|undefined} cfgs Liste normalisierter Configs.
 * @returns {{sender:Array, receiver:Array, other:Array}}
 *
 * @example
 *   bucketByGroup([
 *     {ParameterKey:"a", group:"sender"},
 *     {ParameterKey:"b", group:"receiver"},
 *     {ParameterKey:"c", group:"processing"},
 *     {ParameterKey:"d"} // ohne group
 *   ]);
 *   // -> { sender:[a], receiver:[b], other:[c,d] }
 */
export function bucketByGroup(cfgs) {
  const sender = [];
  const receiver = [];
  const other = [];
  if (!cfgs || !Array.isArray(cfgs)) return { sender, receiver, other };
  for (const c of cfgs) {
    if (!c) continue;
    if (c.group === "sender") sender.push(c);
    else if (c.group === "receiver") receiver.push(c);
    else other.push(c);
  }
  return { sender, receiver, other };
}

/**
 * Extrahiert den Adapter-Namen aus dem ersten Eintrag mit gesetztem adapterType.
 *
 * @param {Array|null|undefined} cfgs Liste normalisierter Configs.
 * @returns {string} Adapter-Name oder "" wenn keiner gefunden.
 *
 * @example
 *   detectAdapter([{adapterType:""}, {adapterType:"HTTPS"}, {adapterType:"SOAP"}]);
 *   // -> "HTTPS"
 */
export function detectAdapter(cfgs) {
  if (!cfgs || !Array.isArray(cfgs)) return "";
  for (const c of cfgs) {
    if (c && c.adapterType) return String(c.adapterType);
  }
  return "";
}

/**
 * BONUS: Baut die Backend-URL für artifact-configs analog zum Modal-Wizard
 * (IntegrationContent.jsx Z. 1162-1164).
 *
 * URL-Schema:
 *   /api/{sourceIid}/transport/artifact-configs/{flowId}
 *   [?targetInstanceId=X&targetFlowId=Y]
 *
 * Query-String wird NUR angehaengt, wenn opts.isUpdate=true UND opts.targetInstanceId
 * gesetzt ist. opts.targetFlowId faellt auf flowId zurück.
 *
 * @param {string} sourceIid          Quell-Instanz-ID.
 * @param {string} flowId             Quell-Artefakt-ID.
 * @param {object} [opts]
 * @param {string} [opts.targetInstanceId]
 * @param {string} [opts.targetFlowId]
 * @param {boolean}[opts.isUpdate]    Default false.
 * @returns {string} Vollstaendige URL.
 *
 * @example
 *   buildArtifactConfigsUrl("src", "Flow1",
 *     { targetInstanceId:"tgt", targetFlowId:"Flow1_DE", isUpdate:true });
 *   // -> "/api/src/transport/artifact-configs/Flow1?targetInstanceId=tgt&targetFlowId=Flow1_DE"
 */
export function buildArtifactConfigsUrl(sourceIid, flowId, opts) {
  const base = `/api/${sourceIid}/transport/artifact-configs/${encodeURIComponent(flowId)}`;
  if (!opts || !opts.isUpdate || !opts.targetInstanceId) return base;
  const tgtFlow = opts.targetFlowId || flowId;
  return `${base}?targetInstanceId=${opts.targetInstanceId}&targetFlowId=${encodeURIComponent(tgtFlow)}`;
}

// ---------------------------------------------------------------------------
// Self-Test (auskommentiert - nur als Referenz für Reviewer):
//
// const sample = [
//   { ParameterKey:"host",  group:"sender",   ParameterValue:"old", targetValue:"new", DataType:"xsd:string", adapterType:"HTTPS" },
//   { ParameterKey:"port",  group:"other",    ParameterValue:"443", targetValue:"",    DataType:"xsd:string" },
//   { ParameterKey:"creds", group:"receiver", ParameterValue:"u",   targetValue:"",    DataType:"xsd:string" }
// ];
// const normalized = sample.map(normalizeConfigEntry);
// console.log("cacheKey ", getArtifactCacheKey("Flow1", "tgt", "werk_DE"));
// console.log("status   ", normalized.map(classifyConfigStatus));
// console.log("basis    ", normalized.map(effektiverBasiswert));
// console.log("validate ", validateRequiredFields(normalized));
// console.log("buckets  ", bucketByGroup(normalized));
// console.log("adapter  ", detectAdapter(normalized));
// console.log("url      ", buildArtifactConfigsUrl("src","Flow1",{targetInstanceId:"tgt",targetFlowId:"Flow1_DE",isUpdate:true}));
